import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Plus, Eye, Copy, Trash2, Link2, CheckCircle2, Clock, AlertTriangle } from "lucide-react";
import { MetabolicCreateModal, MetabolicViewAnswersModal } from "./AdminMetabolicTestsPage";

const STATUS_META = {
  pending: { label: "Aguardando", icon: Clock, cls: "bg-amber-500/15 text-amber-700 border-amber-500/30" },
  submitted: { label: "Respondido", icon: CheckCircle2, cls: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30" },
  expired: { label: "Expirado", icon: AlertTriangle, cls: "bg-muted text-muted-foreground border-border" },
};

export default function PatientMetabolicTestsSection({ patientId, patientName, patientEmail }) {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/patients/${patientId}/metabolic-tests`);
      setTests(data || []);
    } catch (_) {/* noop */}
    setLoading(false);
  }, [patientId]);

  useEffect(() => { load(); }, [load]);

  const copyLink = async (token) => {
    const link = `${window.location.origin}/teste/${token}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link copiado!");
    } catch (_) { toast.error("Copie manualmente: " + link); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Excluir o teste de "${t.patient_name || "sem nome"}"?`)) return;
    try {
      await api.delete(`/admin/metabolic-tests/${t.id}`);
      toast.success("Teste excluído.");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao excluir."); }
  };

  return (
    <div className="bg-card border border-border rounded-sm p-6" data-testid="patient-metabolic-tests-section">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="overline text-muted-foreground mb-1 inline-flex items-center gap-2">
            <FileText className="w-4 h-4" /> Testes metabólicos
          </div>
          <h3 className="font-heading font-semibold">Pré-consulta do paciente</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Envie um formulário e reveja o contexto do paciente antes da próxima consulta.
          </p>
        </div>
        <button
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 rounded-sm bg-primary text-primary-foreground px-3 py-2 text-xs font-semibold hover:bg-primary/90 transition-colors"
          data-testid="patient-mt-new-btn"
        >
          <Plus className="w-3.5 h-3.5" /> Novo teste
        </button>
      </div>

      {loading ? (
        <div className="py-6 text-center text-xs text-muted-foreground overline">Carregando…</div>
      ) : tests.length === 0 ? (
        <div className="py-6 text-center text-sm text-muted-foreground">
          Nenhum teste vinculado. Clique em "Novo teste" para gerar um link.
        </div>
      ) : (
        <div className="space-y-2">
          {tests.map((t) => {
            const meta = STATUS_META[t.status] || STATUS_META.pending;
            const Icon = meta.icon;
            return (
              <div key={t.id} className="flex items-start justify-between gap-3 flex-wrap p-3 rounded-sm border border-border" data-testid={`patient-mt-row-${t.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center flex-wrap gap-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border text-[10px] font-semibold uppercase tracking-wider ${meta.cls}`}>
                      <Icon className="w-3 h-3" /> {meta.label}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      Criado {new Date(t.created_at).toLocaleDateString("pt-BR")}
                      {t.expires_at && <> · Expira {new Date(t.expires_at).toLocaleDateString("pt-BR")}</>}
                    </span>
                  </div>
                  {t.submitted_at && (
                    <div className="text-xs text-emerald-700 mt-1">
                      Respondido em {new Date(t.submitted_at).toLocaleString("pt-BR")}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-none">
                  {t.status === "pending" && (
                    <button onClick={() => copyLink(t.token)}
                      className="p-1.5 rounded-sm border border-border hover:bg-muted transition-colors inline-flex items-center gap-1 text-xs px-2"
                      data-testid={`patient-mt-copy-${t.id}`}>
                      <Link2 className="w-3.5 h-3.5" /> Copiar link
                    </button>
                  )}
                  {t.status === "submitted" && (
                    <button onClick={() => setViewing(t)}
                      className="p-1.5 rounded-sm border border-primary text-primary hover:bg-primary/5 transition-colors inline-flex items-center gap-1 text-xs px-2"
                      data-testid={`patient-mt-view-${t.id}`}>
                      <Eye className="w-3.5 h-3.5" /> Ver respostas
                    </button>
                  )}
                  <button onClick={() => remove(t)}
                    className="p-1.5 rounded-sm border border-destructive/30 text-destructive hover:bg-destructive/5 transition-colors"
                    data-testid={`patient-mt-delete-${t.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {creating && (
        <MetabolicCreateModal
          onClose={() => setCreating(false)}
          onCreated={() => load()}
          presetPatient={{ id: patientId, name: patientName, email: patientEmail }}
        />
      )}
      {viewing && <MetabolicViewAnswersModal test={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}
